<template>
    <div :class="classObject">
        <a
            @click="toggleItem($event)"
            class="accordion-link__header"
            :aria-expanded="state.toString()"
            :aria-controls="`accordion-body-${itemParent.name}`"
        >
            <span
                class="accordion-link__item-inner"
                :class="{'accordion-link__item-inner--active': itemParent.active}"
            >
                <slot/>
                <z-icon
                    :name="arrowIcon"
                    width="20"
                    height="20"
                    dir="down"
                />
            </span>
        </a>
        <transition
            @enter="animationEnter"
            @leave="animationLeave"
        >
            <div
              v-show="state"
              class="accordion-link__body"
              :id="`accordion-body-${itemParent.name}`"
              role="region"
              :aria-label="itemParent.name"
            >
                <ul class="accordion-link__body-list">
                    <li class="accordion-link__body-item" v-if="!itemParent.pageParams.redirect">
                        <a class="accordion-link__body-link" :href="itemParent.link" v-html="text.review" />
                    </li>
                    <li
                        v-for="(item, index) in itemParent.items"
                        :key="`sublink-${itemParent.name}-${itemParent.level}-${index}`"
                        class="accordion-link__body-item"
                    >
                        <accordion-link
                            v-if="item.items.length"
                            :arrow-icon="arrowIcon"
                            :item-parent="item"
                            nested
                        >
                            <span v-html="item.name"/>
                        </accordion-link>
                        <a
                            v-else
                            :href="item.link"
                            :aria-label="item.name"
                            :class="[
                                'accordion-link__body-link',
                                {'accordion-link__body-link--active': item.active},
                            ]"
                            v-html="item.name"
                        />
                    </li>
                </ul>
            </div>
        </transition>
    </div>
</template>

<script>
import Velocity from 'velocity-animate'
import { localize } from '@/utils/i18n'

export default {
    name: 'accordion-link',
    props: {
        itemParent: {
            type: Object,
            required: true
        },
        arrowIcon: {
            type: String,
            required: true
        },
        nested: {
            type: Boolean,
            default: false
        }
    },
    data () {
        return {
            state: false,
            duration: 300,
            text: {
                review: localize({
                    ru: 'Перейти в раздел',
                    en: 'Review',
                    cn: 'Review'
                })
            }
        }
    },
    methods: {
        toggleItem (e) {
            this.state = !this.state

            if (!this.state && e) e.preventDefault()
        },
        animationEnter (el) {
            Velocity(el, 'slideDown', { duration: this.duration })
        },
        animationLeave (el) {
            Velocity(el, 'slideUp', { duration: this.duration })
        },
        scrollIntoView (el) {
            setTimeout(() => {
                el.scrollIntoView({behavior: 'smooth', block: 'start'})
            }, 301)
        }
    },
    computed: {
        classObject () {
            let arrClass = [
                'accordion-link',
                { 'accordion-link--nested': this.nested },
                { 'accordion-link--opened': this.state }
            ]

            return arrClass
        }
    }
}
</script>

<style lang="scss">
@import "../vars";

.accordion-link {
    width: 100%;
    $parent: &;

    &--nested {

        #{$parent}__header {
            padding: 0;
        }

        #{$parent}__item-inner {
            font-weight: 500;
            color: var(--color-link-light-active);
            border-width: 0;
            padding: 0;

            &--active {
                border-style: solid;
                border-color: rgba(255, 255, 255, 0.30);
                border-width: 1px 0 1px 0;
                padding: var(--spacer-2xs);
                margin: 0 -16px;
            }
        }

        #{$parent}__body {
            padding-top: var(--spacer-2xs) !important;
        }

        #{$parent}__body-list {
            padding-bottom: var(--spacer-2xs) !important;
            border-bottom: none !important;
        }

        #{$parent}__body-item {
            font-size: var(--typography-xs);
            font-weight: 400;
        }
    }

    &__header {
        display: block;
        padding: 0 var(--spacer-2xs);
    }

    &__item-inner {
        flex: 0 0 100%;
        font-weight: 700;
        @include typo-level(L);
        padding: var(--spacer-s) 0;
        color: var(--color-white);
        border-width: 0 0 1px 0;
        border-style: solid;
        border-color: rgba(255, 255, 255, 0.30);
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;

        .z-icon {
            path {
                fill: var(--color-link-light-active);
            }
        }

        &--active {
            color: $nav-link-color-active;
            background: rgba(255, 255, 255, 0.10);
            border-width: 1px 0 1px 0;
            padding: var(--spacer-s) var(--spacer-2xs);
            margin: 0 -16px;

            .z-icon {
                path {
                    fill: $nav-link-color-active;
                }
            }
        }
    }

    .z-icon {
        transition: transform ease 300ms;
    }

    &--opened {

        & > #{$parent}__header {

            #{$parent}__item-inner {
                &:not(#{$parent}__item-inner--active) {
                    border-width: 0;
                }
            }

            &:has(#{$parent}__item-inner--active) {
                & + #{$parent}__body {
                    padding-top: var(--spacer-s);
                }
            }

            .z-icon {
                transform: rotate(0deg);
                path {
                    fill: $nav-link-color-active;
                }
            }
        }

        & > #{$parent}__body {
            & > #{$parent}__body-list {
                border-bottom: 1px solid rgba(255, 255, 255, 0.30);
                padding-bottom: var(--spacer-s);
            }
        }
    }

    &__body {}

    &__body-item {
        @include typo-level(M);
        font-weight: 500;
        line-height: 130%;
        color: var(--color-link-light-active);

        &:not(:last-child) {
            margin-bottom: var(--spacer-s);
        }
    }

    &__body-list {
        margin-left: var(--spacer-2xs);
        margin-right: var(--spacer-2xs);
    }

    &__body-link {
        display: block;

        &--active {
            color: var(--color-white) !important;
            text-decoration: underline !important;
        }
    }
}
</style>
